<script setup>
// FarolChat WABA-PRO — drop-in replacement of WhatsappEmbeddedSignup.vue
// Adds mode selector: Coex (recommended) vs Cloud API only.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'next/button/Button.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import globalConstants from 'dashboard/constants/globals.js';
import {
  setupFacebookSdk,
  initWhatsAppEmbeddedSignup,
  createMessageHandler,
  isValidBusinessData,
} from './whatsapp/utils';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const fbSdkLoaded = ref(false);
const isProcessing = ref(false);
const processingMessage = ref('');
const authCodeReceived = ref(false);
const authCode = ref(null);
const businessData = ref(null);
const isAuthenticating = ref(false);

// WABA-PRO: signup mode (coex by default — gives best UX & native business app coexistence)
const signupMode = ref('coex');

const benefits = computed(() => [
  { key: 'EASY_SETUP',  text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.EASY_SETUP') },
  { key: 'SECURE_AUTH', text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.SECURE_AUTH') },
  { key: 'AUTO_CONFIG', text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.AUTO_CONFIG') },
]);

const showLoader = computed(() => isAuthenticating.value || isProcessing.value);

const handleSignupError = data => {
  isProcessing.value = false;
  authCodeReceived.value = false;
  isAuthenticating.value = false;
  const errorMessage = data.error || data.message || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE');
  useAlert(errorMessage);
};

const handleSignupCancellation = () => {
  isProcessing.value = false;
  authCodeReceived.value = false;
  isAuthenticating.value = false;
};

const handleSignupSuccess = inboxData => {
  isProcessing.value = false;
  isAuthenticating.value = false;
  if (inboxData && inboxData.id) {
    useAlert(t('INBOX_MGMT.FINISH.MESSAGE'));
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: inboxData.id },
    });
  } else {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUCCESS_FALLBACK'));
    router.replace({ name: 'settings_inbox_list' });
  }
};

const completeSignupFlow = async businessDataParam => {
  if (!authCodeReceived.value || !authCode.value) {
    handleSignupError({ error: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.AUTH_NOT_COMPLETED') });
    return;
  }

  isProcessing.value = true;
  processingMessage.value = t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.PROCESSING');

  try {
    const params = {
      code: authCode.value,
      business_id: businessDataParam.business_id,
      waba_id: businessDataParam.waba_id,
      phone_number_id: businessDataParam?.phone_number_id || '',
      // WABA-PRO additions
      signup_mode: signupMode.value,
      feature_type: signupMode.value === 'coex' ? 'whatsapp_business_app_onboarding' : null,
    };

    const responseData = await store.dispatch('inboxes/createWhatsAppEmbeddedSignup', params);
    authCode.value = null;
    handleSignupSuccess(responseData);
  } catch (error) {
    const errorMessage = parseAPIErrorResponse(error) || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE');
    handleSignupError({ error: errorMessage });
  }
};

const handleEmbeddedSignupData = async data => {
  if (data.event === 'FINISH' || data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
    const businessDataLocal = data.data;
    if (isValidBusinessData(businessDataLocal)) {
      businessData.value = businessDataLocal;
      if (authCodeReceived.value && authCode.value) {
        await completeSignupFlow(businessDataLocal);
      } else {
        processingMessage.value = t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_AUTH');
      }
    } else {
      handleSignupError({ error: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.INVALID_BUSINESS_DATA') });
    }
  } else if (data.event === 'CANCEL') {
    handleSignupCancellation();
  } else if (data.event === 'error') {
    handleSignupError({
      error: data.error_message || t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SIGNUP_ERROR'),
      error_id: data.error_id,
      session_id: data.session_id,
    });
  }
};

const handleSignupMessage = createMessageHandler(handleEmbeddedSignupData);

const launchEmbeddedSignup = async () => {
  try {
    isAuthenticating.value = true;
    processingMessage.value = t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.AUTH_PROCESSING');

    await setupFacebookSdk(window.chatwootConfig?.whatsappAppId, window.chatwootConfig?.whatsappApiVersion);
    fbSdkLoaded.value = true;

    const code = await initWhatsAppEmbeddedSignup(
      window.chatwootConfig?.whatsappConfigurationId,
      { mode: signupMode.value }
    );

    authCode.value = code;
    authCodeReceived.value = true;
    processingMessage.value = t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_BUSINESS_INFO');

    if (businessData.value) {
      completeSignupFlow(businessData.value);
    }
  } catch (error) {
    if (error.message === 'Login cancelled') {
      isProcessing.value = false;
      isAuthenticating.value = false;
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED'));
    } else {
      handleSignupError({ error: error.message || t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SDK_LOAD_ERROR') });
    }
  }
};

onMounted(() => window.addEventListener('message', handleSignupMessage));
onBeforeUnmount(() => window.removeEventListener('message', handleSignupMessage));
</script>

<template>
  <div class="h-full">
    <LoadingState v-if="showLoader" :message="processingMessage" />

    <div v-else>
      <div class="flex flex-col items-start mb-6 text-start">
        <div class="flex justify-start mb-6">
          <div class="flex size-11 items-center justify-center rounded-full bg-n-alpha-2">
            <Icon icon="i-woot-whatsapp" class="text-n-slate-10 size-6" />
          </div>
        </div>

        <h3 class="mb-2 text-base font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.TITLE') }}
        </h3>
        <p class="text-sm leading-[24px] text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.DESC') }}
        </p>
      </div>

      <!-- WABA-PRO: Mode selector -->
      <div class="mb-6 p-4 rounded-lg border border-n-weak bg-n-alpha-1">
        <p class="mb-3 text-sm font-medium text-n-slate-12">
          Modo de conexão WhatsApp
        </p>
        <div class="flex flex-col gap-2">
          <label class="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-n-alpha-2">
            <input
              v-model="signupMode"
              type="radio"
              value="coex"
              class="mt-1"
              :disabled="isAuthenticating"
            />
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-n-slate-12">Coexistência (recomendado)</span>
                <span class="text-xs px-2 py-0.5 rounded bg-n-brand text-white">novo</span>
              </div>
              <p class="text-xs text-n-slate-11 mt-1">
                Mantenha o app WhatsApp Business no celular E responda pelo Chatwoot. Histórico sincronizado, sem perder mensagens.
              </p>
            </div>
          </label>

          <label class="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-n-alpha-2">
            <input
              v-model="signupMode"
              type="radio"
              value="cloud"
              class="mt-1"
              :disabled="isAuthenticating"
            />
            <div class="flex-1">
              <span class="text-sm font-medium text-n-slate-12">Cloud API (apenas Chatwoot)</span>
              <p class="text-xs text-n-slate-11 mt-1">
                Número exclusivo no Chatwoot. O WhatsApp Business app no celular será desconectado deste número.
              </p>
            </div>
          </label>
        </div>
      </div>

      <div class="flex flex-col gap-2 mb-6">
        <div
          v-for="benefit in benefits"
          :key="benefit.key"
          class="flex gap-2 items-center text-sm text-n-slate-11"
        >
          <Icon icon="i-lucide-check" class="text-n-slate-11 size-4" />
          {{ benefit.text }}
        </div>
      </div>

      <div class="flex flex-col gap-2 mb-6">
        <I18nT
          keypath="INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.LEARN_MORE.TEXT"
          tag="span"
          class="text-sm text-n-slate-11"
        >
          <template #link>
            <a
              :href="globalConstants.WHATSAPP_EMBEDDED_SIGNUP_DOCS_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="underline text-n-brand"
            >
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.LEARN_MORE.LINK_TEXT') }}
            </a>
          </template>
        </I18nT>
      </div>

      <div class="flex mt-4">
        <NextButton
          :disabled="isAuthenticating"
          :is-loading="isAuthenticating"
          faded
          slate
          class="w-full"
          @click="launchEmbeddedSignup"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUBMIT_BUTTON') }}
        </NextButton>
      </div>
    </div>
  </div>
</template>
